<template>
  <BaseModalEditCreate
    :title="$t('groups.addGroupItem', { item: categoryName })"
    mode="select"
    :is-modified="false"
    :close-on-outside-click="false"
    max-width="extra"
    @dismiss-changes="emit('close')"
    @save="emitSelectedRows = true">
    <template #form>
      <div class="overflow-hidden h-full grid grid-rows-[auto_1fr]">
        <div class="font-light">{{ $t('messages.info.ctrlClickToAddItems') }}</div>
        <div class="w-full h-full overflow-x-hidden">
          <BaseTable
            v-if="list"
            table-id="groupItem-materials-table"
            :total-records="totalRecords"
            :headers="categoryHeaders"
            :list="list"
            :emit-selected-rows="emitSelectedRows"
            only-search
            hide-multiselect-delete
            hide-multiselect-download
            @refresh-data="onRefreshData"
            @selected-rows="onMultiselect" />
        </div>
      </div>
    </template>
  </BaseModalEditCreate>
</template>

<script setup lang="ts">
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseModalEditCreate from '../common/BaseModalEditCreate.vue';
import BaseTable from '../common/BaseTable.vue';
import type { AlterCode, Material } from '@/types/Materials';
import type { Customer, TraderSite } from '@/types/Traders';
import type { GroupCategory, TableFilterData } from '@/types/Base';

const props = defineProps<{
  list: Material[] | TraderSite[] | Customer[] | AlterCode[];
  totalRecords?: number;
  category: GroupCategory;
}>();

const emit = defineEmits(['close', 'refreshData', 'multiSelect']);

const { t: $t } = useI18n({ useScope: 'global' });

const materialHeaders = [
  { value: 'name', text: `${$t('baseObject.name')}`, type: 'text', active: true },
  { value: 'code', text: `${$t('baseObject.code')}`, type: 'text', active: true },
  { value: 'smallCode', text: `${$t('material.smallCode')}`, type: 'text', active: true },
  { value: 'smallName', text: `${$t('material.smallName')}`, type: 'text', active: true },
  { value: 'sequence', text: `${$t('material.sequence')}`, type: 'text', active: true },
  { value: 'isActive', text: `${$t('baseObject.isActive')}`, type: 'check', active: true },
];

const traderSiteHeaders = [
  { value: 'code', text: `${$t('baseObject.code')}`, type: 'text', active: true },
  { value: 'name', text: `${$t('baseObject.name')}`, type: 'text', active: true },
  { value: 'trader.name', text: `${$t('customer.traderName')}`, type: 'text', active: true },
  { value: 'address', text: `${$t('address')}`, type: 'text', active: true },
  { value: 'zipCode', text: `${$t('zipCodes')}`, type: 'text', active: true },

  { value: 'isActive', text: `${$t('baseObject.isActive')}`, type: 'check', active: true },
];

const customerHeaders = [
  { value: 'code', text: `${$t('baseObject.code')}`, type: 'text', active: true },
  { value: 'profession', text: `${$t('customer.profession')}`, type: 'text', active: true },
  { value: 'trader.name', text: `${$t('customer.traderName')}`, type: 'text', active: true },
  { value: 'trader.code', text: `${$t('customer.traderCode')}`, type: 'text', active: true },
  {
    value: 'trader.taxNumber',
    text: `${$t('customer.traderTaxNumber')}`,
    type: 'text',
    active: true,
  },
  { value: 'trader.address', text: `${$t('address')}`, type: 'text', active: true },
  { value: 'trader.zipCode', text: `${$t('zipCodes')}`, type: 'text', active: true },
  {
    value: 'defaultPaymentType.name',
    text: 'Default Payment Type',
    type: 'text',
    active: true,
  },
];

const alterCodeHeaders = [
  { value: 'name', text: `${$t('baseObject.name')}`, type: 'text', active: true },
  { value: 'code', text: `${$t('baseObject.code')}`, type: 'text', active: true },
  {
    value: 'alterCodeType.code',
    text: `${$t('material.alterCodeType')}`,
    type: 'text',
    active: true,
  },
  { value: 'isActive', text: `${$t('baseObject.isActive')}`, type: 'status', active: true },
  {
    value: 'isFromManager',
    text: `${$t('baseObject.isFromManager')}`,
    type: 'check',
    active: true,
  },
];

const categoryHeaders = computed(() => {
  switch (props.category) {
    case 'material':
      return materialHeaders;
    case 'traderSite':
      return traderSiteHeaders;
    case 'customer':
      return customerHeaders;
    case 'alterCode':
      return alterCodeHeaders;
    default:
      return [];
  }
});

const categoryName = computed(() => {
  switch (props.category) {
    case 'material':
      return `${$t('pages.materials')}`;
    case 'traderSite':
      return `${$t('discountMatrix.traderSite')}`;
    case 'customer':
      return `${$t('pages.customers')}`;
    case 'alterCode':
      return `${$t('groups.alterCode')}`;
    default:
      return '';
  }
});

function onRefreshData(data: TableFilterData) {
  data.sortColumn = data.column ? categoryHeaders.value[data.column].value : '';
  emit('refreshData', data);
}

const emitSelectedRows = ref(false);
function onMultiselect(items: Material[] | TraderSite[] | Customer[] | AlterCode[]) {
  emit('multiSelect', items);
}
</script>
